export type AnalyticsEvent = "Signup" | "StoreConnected" | "LandingCta";

export type LandingCtaPlacement = "header" | "hero" | "plan" | "footer";

type PlausibleFn = ((
  event: string,
  options?: { u?: string; props?: Record<string, string> },
) => void) & { q?: unknown[] };

declare global {
  interface Window {
    plausible?: PlausibleFn;
  }
}

function plausibleDomain() {
  return (import.meta.env.VITE_PLAUSIBLE_DOMAIN as string | undefined)?.trim();
}

function plausibleSrc() {
  return (
    (import.meta.env.VITE_PLAUSIBLE_SRC as string | undefined)?.trim() ||
    "https://plausible.io/js/script.manual.js"
  );
}

export function analyticsEnabled() {
  return Boolean(plausibleDomain());
}

export function initAnalytics() {
  const domain = plausibleDomain();
  if (!domain || typeof document === "undefined") return;
  if (document.querySelector(`script[data-domain="${domain}"]`)) return;

  const stub = ((...args: unknown[]) => {
    (stub.q ??= []).push(args);
  }) as PlausibleFn;
  window.plausible ??= stub;

  const script = document.createElement("script");
  script.defer = true;
  script.dataset.domain = domain;
  script.src = plausibleSrc();
  document.head.appendChild(script);
}

export function trackPageview(pathname: string) {
  if (!analyticsEnabled() || typeof window === "undefined") return;
  window.plausible?.("pageview", { u: `${window.location.origin}${pathname}` });
}

export function trackEvent(
  name: AnalyticsEvent,
  props?: Record<string, string>,
) {
  if (!analyticsEnabled() || typeof window === "undefined") return;
  window.plausible?.(name, props ? { props } : undefined);
}

export function trackLandingCta(placement: LandingCtaPlacement) {
  trackEvent("LandingCta", { placement });
}

export function trackEventOnce(name: AnalyticsEvent, key: string) {
  if (typeof sessionStorage === "undefined") {
    trackEvent(name);
    return;
  }
  if (sessionStorage.getItem(key)) return;
  sessionStorage.setItem(key, "1");
  trackEvent(name);
}
